// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useEffect} from 'react'
import {showVersionMismatchToast} from './version-mismatch-notification'
import {versionMismatchDetector} from './version-mismatch-detector'

interface VersionMismatchToastProviderProps {
  appName: string
  children: React.ReactNode
}

/**
 * React component that provides version mismatch detection with toast notifications.
 * Must be placed inside a ToastProvider to have access to toast context.
 *
 * @example
 * ```tsx
 * <ToastProvider>
 *   <VersionMismatchToastProvider appName="issues">
 *     <App />
 *   </VersionMismatchToastProvider>
 * </ToastProvider>
 * ```
 */
export function VersionMismatchToastProvider({appName, children}: VersionMismatchToastProviderProps) {
  const toastContext = useToastContext()

  useEffect(() => {
    versionMismatchDetector.registerApp(appName, {
      onMismatchDetected: () => {
        showVersionMismatchToast(toastContext)
      },
    })

    // Cleanup: unregister when component unmounts to prevent stale closures
    return () => {
      versionMismatchDetector.unregisterApp(appName)
    }
  }, [appName, toastContext])

  return <>{children}</>
}
